/*
 * vanilla-match-height 1.0.0
 * A lightweight, jQuery-free port of jquery-match-height 0.7.2
 * MIT License
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define([], factory);
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.vMatchHeight = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // -------------------------
  // Utilities
  // -------------------------
  const _groups = [];
  let _viewportW = -1;
  let _timer = -1;
  const _throttleMs = 80;

  const px = v => parseFloat(v) || 0;

  const getStyle = (el) => getComputedStyle(el);

  const outerHeight = (el) => {
    // Like jQuery outerHeight(false): includes padding + border, excludes margin
    const cs = getStyle(el);
    let h;
    if (cs.boxSizing === 'border-box') {
      // offsetHeight includes padding + border
      h = el.offsetHeight;
    } else {
      // content-box: clientHeight = content + padding, add borders
      h = el.clientHeight + px(cs.borderTopWidth) + px(cs.borderBottomWidth);
    }
    // If element is temporarily set to height:100px during measurement (like original plugin),
    // offsetHeight will reflect that; we'll reset styles later.
    return h;
  };

  const setHeightProperty = (el, prop, targetPx) => {
    const cs = getStyle(el);
    let adjust = 0;
    if (cs.boxSizing !== 'border-box') {
      adjust += px(cs.borderTopWidth) + px(cs.borderBottomWidth);
      adjust += px(cs.paddingTop) + px(cs.paddingBottom);
    }
    el.style[prop] = Math.max(targetPx - adjust, 0) + 'px';
  };

  // Group elements by visual row (top offsets within 1px)
  const groupByRow = (elements) => {
    const rows = [];
    let lastTop = null;
    const tolerance = 1; // px
    elements.forEach(el => {
      const cs = getStyle(el);
      const rect = el.getBoundingClientRect();
      const docTop = rect.top + (window.pageYOffset || document.documentElement.scrollTop);
      const topWithMargin = docTop - px(cs.marginTop);
      const last = rows.length ? rows[rows.length - 1] : null;
      if (!last) {
        rows.push([el]);
        lastTop = topWithMargin;
      } else if (Math.floor(Math.abs(topWithMargin - lastTop)) <= tolerance) {
        last.push(el);
      } else {
        rows.push([el]);
        lastTop = topWithMargin;
      }
    });
    return rows;
  };

  // Temporarily reveal hidden ancestors so measurements are possible
  const revealHiddenAncestors = (elements) => {
    const ancestors = new Set();
    elements.forEach(el => {
      let p = el.parentElement;
      while (p) {
        if (getStyle(p).display === 'none') ancestors.add(p);
        p = p.parentElement;
      }
    });
    const cache = new Map();
    ancestors.forEach(a => {
      cache.set(a, a.getAttribute('style'));
      a.style.display = 'block';
    });
    return () => {
      ancestors.forEach(a => {
        const prev = cache.get(a);
        if (prev === null || prev === undefined) a.removeAttribute('style');
        else a.setAttribute('style', prev);
      });
    };
  };

  // Normalize display for measurement (mirrors original logic)
  const normalizeForMeasure = (els) => {
    const cache = new Map();
    els.forEach(el => {
      cache.set(el, el.getAttribute('style'));
      const d = getStyle(el).display;
      const normalized = (d === 'inline-block' || d === 'flex' || d === 'inline-flex') ? d : 'block';
      Object.assign(el.style, {
        display: normalized,
        paddingTop: '0',
        paddingBottom: '0',
        marginTop: '0',
        marginBottom: '0',
        borderTopWidth: '0',
        borderBottomWidth: '0',
        height: '100px',
        overflow: 'hidden'
      });
    });
    return () => {
      els.forEach(el => {
        const prev = cache.get(el);
        if (prev === null || prev === undefined) el.removeAttribute('style');
        else el.setAttribute('style', prev);
      });
    };
  };

  const toArray = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (NodeList.prototype.isPrototypeOf(input) || HTMLCollection.prototype.isPrototypeOf(input)) {
      return Array.from(input);
    }
    if (typeof input === 'string') return Array.from(document.querySelectorAll(input));
    if (input.nodeType === 1) return [input];
    return [];
  };

  // -------------------------
  // Core apply
  // -------------------------
  function _apply(elements, options) {
    const opts = Object.assign({
      byRow: true,
      property: 'height',
      target: null,  // HTMLElement or selector
      remove: false
    }, options || {});

    const els = toArray(elements);
    if (!els.length) return;

    if (opts.remove) {
      els.forEach(el => { el.style[opts.property] = ''; });
      // remove from groups
      _groups.forEach(g => { g.elements = g.elements.filter(e => !els.includes(e)); });
      return;
    }

    // Resolve target
    let targetEl = null;
    if (opts.target) {
      targetEl = typeof opts.target === 'string' ? document.querySelector(opts.target) : opts.target;
    }

    // Reveal hidden ancestors so we can measure
    const restoreHidden = revealHiddenAncestors(els);

    // For byRow without target, normalize for measurement and compute rows
    let rowSets;
    if (opts.byRow && !targetEl) {
      const restoreNormals = normalizeForMeasure(els);
      rowSets = groupByRow(els);
      restoreNormals();
    } else {
      rowSets = [els];
    }

    rowSets.forEach(row => {
      if (!row.length) return;

      // If byRow and the row has only one element and no target, clear height to auto
      if (opts.byRow && !targetEl && row.length <= 1) {
        row[0].style[opts.property] = '';
        return;
      }

      // Find max height
      let max = 0;
      if (targetEl) {
        max = outerHeight(targetEl);
      } else {
        // Clear property to measure natural heights
        row.forEach(el => {
          const prev = el.getAttribute('style');
          const d = getStyle(el).display;
          const normalized = (d === 'inline-block' || d === 'flex' || d === 'inline-flex') ? d : 'block';
          el.style.display = normalized;
          el.style[opts.property] = '';
          // measure
          const h = outerHeight(el);
          if (h > max) max = h;
          // restore display if it was empty
          if (!prev || !/display\s*:/.test(prev)) el.style.display = '';
          if (prev) el.setAttribute('style', prev);
          else if (!el.getAttribute('style')) el.removeAttribute('style');
        });
      }

      // Apply height to each element in the row
      row.forEach(el => {
        if (targetEl && el === targetEl) return; // don't set on the target itself
        setHeightProperty(el, opts.property, max);
      });
    });

    // restore hidden ancestors
    restoreHidden();
  }

  // -------------------------
  // Public API
  // -------------------------
  function matchHeight(elements, options) {
    const els = toArray(elements);
    if (els.length <= 1 && !(options && options.target)) {
      _apply(els, options); // still allow remove on single
      return;
    }
    _groups.push({ elements: els, options: Object.assign({}, options) });
    _apply(els, options);
  }

  matchHeight.update = function (fromResizeEvent) {
    // Throttle on resize
    if (fromResizeEvent) {
      const w = window.innerWidth || document.documentElement.clientWidth;
      if (w === _viewportW) return;
      _viewportW = w;
    }
    const run = () => {
      _groups.forEach(g => _apply(g.elements, g.options));
    };
    if (fromResizeEvent) {
      if (_timer !== -1) return;
      _timer = setTimeout(() => { run(); _timer = -1; }, _throttleMs);
    } else {
      run();
    }
  };

  matchHeight.remove = function (elements, property = 'height') {
    const els = toArray(elements);
    _apply(els, { remove: true, property });
  };

  // -------------------------
  // Data API
  // -------------------------
  function applyDataApi() {
    const buckets = new Map();
    document.querySelectorAll('[data-match-height], [data-mh]').forEach(el => {
      const key = el.getAttribute('data-mh') || el.getAttribute('data-match-height');
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(el);
    });
    buckets.forEach(group => matchHeight(group, { byRow: true }));
  }

  // Init on DOM ready (like original plugin)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyDataApi);
  } else {
    applyDataApi();
  }

  // Recalculate on load + resize/orientationchange
  window.addEventListener('load', () => matchHeight.update(false));
  window.addEventListener('resize', (e) => matchHeight.update(true));
  window.addEventListener('orientationchange', (e) => matchHeight.update(true));

  // Expose
  return matchHeight;
}));
